// Google Analytics 4 tracking utility
// Measurement ID: G-TZH9WG1Q62
// Conversion Funnel: blog_post_read → calculator_started → calculator_completed → lead_submitted

const GA_MEASUREMENT_ID = 'G-TZH9WG1Q62';

// Initialize GA4 - called once on app load
export const initGA = () => {
  if (typeof window === 'undefined') return;
  
  // Check if gtag is already loaded
  if (window.gtag) return;
  
  // Load the GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false // We'll track page views manually via router
  });
};

// Track page views
export const trackPageView = (path, title) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.href
  });
};

// Custom event tracking
export const trackEvent = (eventName, params = {}) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', eventName, params);
};

// ==========================================
// CONVERSION FUNNEL EVENTS (marked as conversions in GA4)
// Funnel: blog_post_read → calculator_started → calculator_completed → lead_submitted
// ==========================================

// Step 1: Blog Post Read (entry point)
export const trackBlogPostRead = (slug, title, category) => {
  trackEvent('blog_post_read', {
    post_slug: slug,
    post_title: title,
    post_category: category,
    funnel_step: 1,
    funnel_name: 'lead_generation'
  });
};

// Step 2: Calculator Started (user begins forecast)
export const trackCalculatorStarted = (source = 'direct') => {
  trackEvent('calculator_started', {
    source: source,
    funnel_step: 2,
    funnel_name: 'lead_generation'
  });
};

// Step 3: Calculator Completed (user sees results) - CONVERSION EVENT
export const trackCalculatorCompleted = (answers = {}) => {
  trackEvent('calculator_completed', {
    current_weight: answers.currentWeight || 'not_provided',
    goal_weight: answers.goalWeight || 'not_provided',
    medication: answers.medication || 'not_provided',
    activity_level: answers.activityLevel || 'not_provided',
    funnel_step: 3,
    funnel_name: 'lead_generation',
    conversion: true
  });
};

// Step 4: Lead Submitted (form completed) - CONVERSION EVENT
export const trackLeadSubmitted = (leadData = {}) => {
  trackEvent('lead_submitted', {
    primary_goal: leadData.primaryGoal || 'not_provided',
    interested_in: leadData.interestedIn || 'not_provided',
    funnel_step: 4,
    funnel_name: 'lead_generation',
    conversion: true
  });
};

// ==========================================
// ENGAGEMENT EVENTS
// ==========================================

export const trackTextButtonClicked = () => {
  trackEvent('text_button_clicked', {
    button_location: 'calculator_results',
    engagement_type: 'high_intent'
  });
};

export const trackCalendarButtonClicked = () => {
  trackEvent('calendar_button_clicked', {
    button_location: 'calculator_results',
    engagement_type: 'high_intent'
  });
};

export const trackShareButtonClicked = (method) => {
  trackEvent('share_button_clicked', {
    share_method: method,
    engagement_type: 'referral'
  });
};

export const trackForecastCtaClicked = (location) => {
  trackEvent('forecast_cta_clicked', {
    cta_location: location,
    engagement_type: 'navigation'
  });
};

// ==========================================
// FUNNEL ANALYSIS HELPER
// ==========================================

// Get funnel position from sessionStorage to track user journey
export const getFunnelPosition = () => {
  if (typeof window === 'undefined') return 0;
  return parseInt(sessionStorage.getItem('funnel_position') || '0', 10);
};

export const setFunnelPosition = (step) => {
  if (typeof window === 'undefined') return;
  const currentStep = getFunnelPosition();
  // Only update if moving forward in funnel
  if (step > currentStep) {
    sessionStorage.setItem('funnel_position', step.toString());
  }
};

// Track funnel progression
export const trackFunnelProgression = (fromStep, toStep) => {
  trackEvent('funnel_progression', {
    from_step: fromStep,
    to_step: toStep,
    funnel_name: 'lead_generation'
  });
};

// ==========================================
// STRENGTH PLAN BUILDER EVENTS
// ==========================================

const getSourcePage = () => {
  if (typeof document === 'undefined') return 'direct';
  return document.referrer || 'direct';
};

export const trackStrengthStarted = (params = {}) => {
  trackEvent('strength_tool_started', { ...params, source_page: getSourcePage() });
};

export const trackStrengthCompleted = (params = {}) => {
  trackEvent('strength_tool_completed', { ...params, source_page: getSourcePage() });
};

export const trackStrengthGoalSelected = (goal, params = {}) => {
  trackEvent('strength_goal_selected', { goal, ...params, source_page: getSourcePage() });
};

export const trackStrengthCtaClicked = (button, params = {}) => {
  trackEvent('strength_plan_cta_clicked', { button, ...params, source_page: getSourcePage() });
};

export const trackStrengthLadderOpened = (ladderName, params = {}) => {
  trackEvent('strength_ladder_opened', { ladder_name: ladderName, ...params, source_page: getSourcePage() });
};

export const trackStrengthEquipmentSelected = (equipmentType, params = {}) => {
  trackEvent('strength_equipment_selected', { equipment_type: equipmentType, ...params, source_page: getSourcePage() });
};

export const trackStrengthWorkoutCopied = (day, equipmentType, params = {}) => {
  trackEvent('strength_workout_copied', { day, equipment_type: equipmentType, ...params, source_page: getSourcePage() });
};

export const trackStrengthRecoveryViewed = (params = {}) => {
  trackEvent('strength_recovery_section_viewed', { ...params, source_page: getSourcePage() });
};
